// Typed template endpoint functions. Backed by GET /api/v1/templates/*.
//
// Per CHG-1261 plan — consumed by @generated/screens/template-list.ts +
// template-detail.ts queryFn callbacks. Spec:
// specs/tier-05-experience/template.entity-hint.yaml (CHG-1235).
//
// Canonical v4 API path is `/templates` per the entity-hint detail tabs
// `GET /templates/{id}/versions` + `GET /templates/{id}/messages`. v1
// runtime currently serves `/settings/notification-templates` with
// slug-based detail — if the canonical /templates router isn't yet
// shipped, a backend bridge CHG lands later. The v2 client targets the
// canonical path; same pattern as CHG-1251 designation sub-resource
// bridges.
//
// EDITOR TAB mutation — CHG-1285 introduced the v2 editor against a
// forward-promised PATCH /templates/{id}; CHG-1288 (this CHG) shipped
// the real backend endpoint at app/routers/v2_templates.py. The
// safeFetch friendly-notice branch that surfaced "Edits will persist
// when backend ships" on 404/501 has been retired; failures now flow
// through the normal optimistic-rollback path with the BE error detail.

import { apiClient } from "./client";
import { getActiveOrgId } from "../contexts/OrgContext";
import type {
  Template,
  TemplatePagedEnvelope,
  TemplateVersion,
  TemplateMessageBinding,
  TemplateStatus,
  TemplateTone,
  PagedEnvelope,
} from "./types";

// CHG-1648 / ITER-3757 — canonical BE read path is
// /api/v1/organizations/{org_id}/templates (templates.py prefix). The
// v2 client previously called bare "/templates" which 404s — only
// v2_templates.py serves /api/v1/templates and it only declares a
// single PATCH /{id} endpoint, not the list/get/versions/bindings GETs.
// Reads must go through the org-scoped router; the PATCH below
// (updateTemplate) keeps targeting the v2_templates canonical path.
function templatesBase(): string {
  const orgId = getActiveOrgId() ?? "current";
  return `/organizations/${orgId}/templates`;
}

export interface ListTemplatesParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  status?: string;
  purpose?: string;
  tone?: string;
}

export async function listTemplates(
  params: ListTemplatesParams = {},
): Promise<{ items: Template[]; total: number }> {
  const r = await apiClient().get<TemplatePagedEnvelope>(templatesBase(), {
    params: {
      offset: (params.page ?? 0) * (params.pageSize ?? 50),
      limit: params.pageSize ?? 50,
      sort: params.sortField
        ? `${params.sortField}:${params.sortDirection ?? "asc"}`
        : undefined,
      q: params.search,
      status: params.status,
      purpose: params.purpose,
      tone: params.tone,
    },
  });
  return { items: r.data.items, total: r.data.total };
}

export async function getTemplate(id: string): Promise<Template> {
  const r = await apiClient().get<Template>(`${templatesBase()}/${id}`);
  return r.data;
}

// Editor + History tabs pull the version list; the Editor tab renders
// the latest version's subject/body as a read-only preview.
export async function listTemplateVersions(
  id: string,
): Promise<PagedEnvelope<TemplateVersion>> {
  const r = await apiClient().get<PagedEnvelope<TemplateVersion>>(
    `${templatesBase()}/${id}/versions`,
  );
  return r.data;
}

// Bindings tab — back-reference index from template-bridge service. Each
// row records a (message_kind, consumer_kind, consumer_id) tuple where
// this template is used.
export async function listTemplateBindings(
  id: string,
): Promise<PagedEnvelope<TemplateMessageBinding>> {
  const r = await apiClient().get<PagedEnvelope<TemplateMessageBinding>>(
    `${templatesBase()}/${id}/messages`,
  );
  return r.data;
}

// CHG-1285/1288 — partial-update patch shape consumed by the v2
// TemplateEditor. extra='forbid' on the BE V2TemplatePatch mirrors this
// allowlist; FE caps tighter than BE per feedback_fe_be_validation_must_match.md.
export interface TemplateUpdatePatch {
  subject?: string;        // max 200 FE / 500 BE (V2TemplatePatch)
  body?: string;           // max 50_000 FE + BE
  tone?: TemplateTone;
  status?: TemplateStatus;
}

/**
 * CHG-1288 — Mutate a template via PATCH /templates/{id}.
 *
 * Backend endpoint shipped in CHG-1288 (app/routers/v2_templates.py).
 * Returns the updated Template. The wrapper re-throws axios errors so
 * the caller (TemplateEditor) can run an optimistic-update + rollback
 * pattern.
 */
export async function updateTemplate(
  id: string,
  patch: TemplateUpdatePatch,
): Promise<Template> {
  const r = await apiClient().patch<Template>(`/templates/${id}`, patch);
  return r.data;
}

export const templatesApi = {
  listTemplates,
  getTemplate,
  listTemplateVersions,
  listTemplateBindings,
  updateTemplate,
};
